import React, { FC, useEffect, useRef, useState } from "react";
import classnames from "classnames";
import { LEADERBOARD_PERIOD } from "@shared/models";

import "./index.scss";

interface LeaderboardPeriodSelectProps {
  period: LEADERBOARD_PERIOD;
  onChange: (period: LEADERBOARD_PERIOD) => void;
  // An optional modifier class, so the Leaderboard TAB can restyle this same control for its own
  // wider column (a 130px bordered white trigger, a right-aligned menu) without a second dropdown
  // being built -- the same "restyle without rebuilding" mechanism `LeaderboardBoardToggle`'s own
  // `className` prop already provides for the other control on both screens. The Overview rail card
  // passes nothing and keeps the base pill below.
  className?: string;
}

// Figma `Leaderboard` (9119:1089): the control's own copy ("This week"/"This month"/"This quarter"/
// "All time") differs from the enum's own wire values ("Week"/"Month"/"Quarter"/"All-time",
// Leaderboard.tsx's own comment: "Byte-for-byte the api's own LEADERBOARD_PERIOD"). Keyed on the enum
// MEMBER, never its string value, so this label map can't drift out of step with a future wire-value
// rename, and a hardcoded label can't be written down where a lookup belongs.
//
// This is the ONE place the four period labels exist. They are product copy on two screens (the
// Overview rail card's head and the Leaderboard tab's toggle row) -- a second copy of this map is
// exactly how the two screens would come to say different things about the same period.
const PERIOD_LABELS: Record<LEADERBOARD_PERIOD, string> = {
  [LEADERBOARD_PERIOD.WEEK]: "This week",
  [LEADERBOARD_PERIOD.MONTH]: "This month",
  [LEADERBOARD_PERIOD.QUARTER]: "This quarter",
  [LEADERBOARD_PERIOD.ALL_TIME]: "All time",
};

// Drives the open menu's option order.
const PERIOD_OPTIONS: LEADERBOARD_PERIOD[] = [
  LEADERBOARD_PERIOD.WEEK,
  LEADERBOARD_PERIOD.MONTH,
  LEADERBOARD_PERIOD.QUARTER,
  LEADERBOARD_PERIOD.ALL_TIME,
];

/**
 * The trigger's chevron, inline SVG for the same reason `CardHead`'s arrow and `PodiumTile`'s crown
 * are: this app's `Icon` bakes colour into the file, and this one needs to take the trigger's own text
 * colour (`currentColor`) and flip 180deg when the menu is open (index.scss's own
 * `.leaderboard-period-select-trigger[aria-expanded="true"]` rule) -- neither is possible with a baked
 * background image.
 */
const LeaderboardPeriodSelectChevron: FC = () => (
  <svg
    className="leaderboard-period-select-chevron"
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M3 4.5L6 7.5L9 4.5"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * The leaderboard period control (Figma `Leaderboard`, 9119:1089): a single pill showing the current
 * period, opening a listbox of all four below it on click. Shared by BOTH leaderboard screens under
 * `containers/Recognition/` -- the Overview rail card's head (`OverviewLeaderboard`) and the
 * Leaderboard tab's toggle row (`LeaderboardsContainer`) -- with the tab's own wider treatment applied
 * through `className`, not through a second component. It was briefly two near-identical components
 * (`ScopeSelect` on the card, `LeaderboardPeriodToggle` on the tab); this is that pair collapsed, so
 * the four period labels above live in exactly one place.
 *
 * Named `...Select`, not `...Toggle`: it is a dropdown. The old `LeaderboardPeriodToggle` name
 * described the four-button row this replaced.
 *
 * Presentational only: no dispatch/select of its own, `period`/`onChange` are plain props, and each
 * screen owns its own period state (the tab's lives in the sparks slice, the card's in `useState`).
 *
 * @param props.period - the currently selected period; shown as the trigger's own label.
 * @param props.onChange - called with the clicked option's enum VALUE, never its display label.
 * @param props.className - optional modifier class, merged with the base class rather than replacing
 *   it; the Leaderboard tab's own wider treatment.
 */
const LeaderboardPeriodSelect: FC<LeaderboardPeriodSelectProps> = (props) => {
  const { period, onChange, className } = props;
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Escape and outside-click both close the menu. Listeners are added only while OPEN (and removed on
  // close/unmount), so a closed control -- the common case -- costs nothing.
  //
  // Deliberately NOT `useOutsideClick` (src/shared/hooks/OutsideClickHook.tsx), even though it exists
  // and is used elsewhere in this app -- two concrete reasons, not just "hand-rolling felt easier":
  // (1) that hook's own `useEffect` has NO dependency array, so it rebinds a document listener on
  // EVERY render, and that listener stays bound even while the menu this component owns is CLOSED;
  // (2) it exposes `{ isOutside, setOutsideValue }` -- a piece of state with a TOGGLE setter and no
  // close callback -- so wiring "close on outside click" through it would still need an extra effect
  // watching `isOutside` plus a manual reset back to `false` afterwards, which is MORE moving parts,
  // and more ways for the two pieces of state to desync, than the listener below (bound only while
  // `isOpen`, closes directly, no intermediate state to keep in sync).
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleOutsideClick = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const handleSelect = (option: LEADERBOARD_PERIOD) => {
    onChange(option);
    setIsOpen(false);
  };

  return (
    <div className={classnames("leaderboard-period-select", className)} ref={rootRef}>
      <button
        type="button"
        className="leaderboard-period-select-trigger"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
      >
        {PERIOD_LABELS[period]}
        <LeaderboardPeriodSelectChevron />
      </button>
      {isOpen ? (
        <div className="leaderboard-period-select-menu" role="listbox">
          {PERIOD_OPTIONS.map((option) => (
            <button
              key={option}
              type="button"
              role="option"
              aria-selected={option === period}
              className={classnames("leaderboard-period-select-option", { selected: option === period })}
              onClick={() => handleSelect(option)}
            >
              {PERIOD_LABELS[option]}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default LeaderboardPeriodSelect;
